import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Icon } from '../../design-system/icons';
import {
  captureAttributionParams,
  metadataDeAtribucion,
} from '../../lib/signupAttribution';
import { getPostLoginDest } from '../../lib/postLoginDest';
import { useInvitacionReferido } from '../../hooks/useInvitacionReferido';
import { recordarVueltaDelReset } from '../../lib/vueltaAlAlta';
import { useBfcacheRestore } from '../../hooks/useBfcacheRestore';
import { leerUltimoLogin, type MetodoLogin } from '../../lib/ultimoLogin';

/**
 * Formulario de autenticación del onboarding **sin contenedor**: sólo el contenido,
 * para montarlo en los dos envases que existen (ADR-2026-07-30-registro-pantalla-completa):
 *
 * - `variant="sheet"` → dentro de `AuthSheet` (panel izquierdo fijo de **desktop**).
 * - `variant="page"` → pantalla completa de mobile (`pages/Registro.tsx` y `/auth`).
 *
 * Reemplaza a `pages/Auth.tsx` (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real), así
 * que hereda TODO lo que aquella hacía y que este componente no tenía:
 * `?next=` (destino post-login), la pantalla de **confirmación de email** con reenvío
 * y cooldown, y la captura de atribución al montar. Lo que NO se hereda, a propósito:
 * el checkbox obligatorio de términos — el diseño nuevo usa aceptación implícita.
 *
 * Estilos: tokens del DS v3 vía utilidades Tailwind arbitrarias con el hex del token
 * (permiten estados `hover/focus`), documentados con el nombre del token.
 */

export type AuthMode = 'signup' | 'login' | 'forgot' | 'confirmation';

// Términos y condiciones → misma URL a la que redirige hoy `Auth.tsx`.
const TERMS_URL = 'https://estudio-atlas.com/politica-de-privacidad';

const TITLES: Record<AuthMode, string> = {
  signup: 'Crear cuenta',
  login: 'Ingresá a tu cuenta',
  forgot: 'Restablecer contraseña',
  confirmation: 'Confirmá tu email',
};

export interface AuthFormProps {
  /** Modo visible. En el sheet lo maneja `AuthSheets`; en la página, `Registro`. */
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  /** id del título, para el `aria-labelledby` del contenedor. */
  titleId: string;
  /** Envase donde se monta (define el ancho/padding y el nivel del título). */
  variant?: 'sheet' | 'page';
}

export function AuthForm({ mode, onModeChange, titleId, variant = 'sheet' }: AuthFormProps) {
  /* Quién invitó, si el visitante llegó por un link de referido
     (ADR-2026-08-27-programa-referidos). Es DECORATIVO: si la consulta falla, el
     hook devuelve null y la pantalla de alta queda idéntica a como estaba. El
     código en sí ya lo guardó `captureAttributionParams`, así que el alta
     funciona igual aunque este cartel no aparezca nunca. */
  const invitacion = useInvitacionReferido();
  const navigate = useNavigate();
  const isPage = variant === 'page';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resetCooldown, setResetCooldown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  // Destino post-login (`?next=`), capturado UNA sola vez al montar: si se
  // recalculara en cada navegación, la segunda leería la URL ya sin el param.
  const [postLoginDest] = useState(getPostLoginDest);

  // Método usado la última vez en este dispositivo, leído UNA vez al montar: sólo
  // cambia al abrirse una sesión, y para entonces esta pantalla ya no está.
  const [ultimoLogin] = useState(leerUltimoLogin);

  // Limpia el feedback al cambiar de modo (los errores no deben persistir).
  useEffect(() => {
    setError(null);
    setMessage(null);
  }, [mode]);

  // Cooldowns del reset y del reenvío de confirmación (evitan spamear el email).
  useEffect(() => {
    if (!resetCooldown) return;
    const id = setInterval(() => {
      setResetCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resetCooldown]);

  useEffect(() => {
    if (!resendCooldown) return;
    const id = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Reenvío del correo de confirmación (pantalla `confirmation`).
  const handleResend = async () => {
    const mail = email.trim();
    if (!mail || resendCooldown > 0 || resendLoading) return;
    setError(null);
    setMessage(null);
    setResendLoading(true);
    try {
      const { error: err } = await supabase.auth.resend({ type: 'signup', email: mail });
      if (err) throw err;
      setMessage('Te enviamos un nuevo correo de confirmación.');
      setResendCooldown(60);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo reenviar el correo de confirmación',
      );
    } finally {
      setResendLoading(false);
    }
  };

  const switchMode = (next: AuthMode) => {
    setError(null);
    setMessage(null);
    onModeChange(next);
  };

  // Atribución (UTM / ref / cookies de Meta): TODA la lógica vive en
  // `lib/signupAttribution.ts` — este componente no tiene copia propia
  // (ADR-2026-07-31-atribucion-una-sola-implementacion).
  //
  // La captura corre al MONTAR, no al hacer submit: si el usuario aterriza con
  // `?utm_source=…&ref=…`, los datos quedan en localStorage aunque después navegue
  // entre el carrusel y el registro, o tarde en completar el form.
  useEffect(() => {
    captureAttributionParams();
  }, []);

  const handleGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth` },
      });
      if (err) throw err;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error al continuar con Google',
      );
      setGoogleLoading(false);
    }
  };

  // Volver con "atrás" desde la pantalla de Google: la página vuelve del bfcache con
  // el estado congelado y el botón seguía girando para siempre. El `catch` de arriba
  // no lo salva — esa promesa quedó abandonada cuando el documento se congeló, así que
  // el único `setGoogleLoading(false)` nunca llega.
  // Ver ADR-2026-08-11-bfcache-boton-google-colgado.
  useBfcacheRestore(() => setGoogleLoading(false));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    const mail = email.trim();
    if (!mail) {
      setError('Ingresá tu correo electrónico');
      return;
    }
    if (mode !== 'forgot' && !password) {
      setError('Ingresá tu contraseña');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const { error: err } = await supabase.auth.signInWithPassword({
          email: mail,
          password,
        });
        if (err) {
          if (err.message?.includes('Email not confirmed')) {
            throw new Error(
              'Tu email todavía no está confirmado. Revisá tu bandeja de entrada.',
            );
          }
          if (err.message === 'Invalid login credentials') {
            throw new Error('Correo electrónico o contraseña incorrectos');
          }
          throw err;
        }
        // `?next=` del plugin de Premiere; sin él, al home.
        navigate(postLoginDest, { replace: true });
      } else if (mode === 'signup') {
        // UTMs, cookies de Meta y ref_code viajan en la metadata y los baja el trigger
        // (spec 2026-09-11-invitaciones-equipo §5.3): así llegan aunque haya que confirmar el mail.
        const metadata: Record<string, string> = { ...metadataDeAtribucion() };
        if (name.trim()) metadata.full_name = name.trim();
        const { data, error: err } = await supabase.auth.signUp({
          email: mail,
          password,
          ...(Object.keys(metadata).length ? { options: { data: metadata } } : {}),
        });
        if (err) throw err;
        if (data?.session) {
          navigate(postLoginDest, { replace: true });
        } else {
          // Sin sesión = Supabase exige confirmar el mail. Pantalla propia con el
          // reenvío del correo, igual que el `Auth.tsx` que este flujo reemplaza.
          onModeChange('confirmation');
        }
      } else {
        // forgot
        if (resetCooldown > 0) {
          setError(`Esperá ${resetCooldown}s antes de pedir otro email.`);
          setLoading(false);
          return;
        }
      // Cualquier recuperación anota (o BORRA) el destino del alta por link: si esto no
      // corriera acá, un reset del alta abandonado a la mitad mandaría a esta persona a
      // un plan que no está comprando. Ver `lib/vueltaAlAlta.ts`.
      recordarVueltaDelReset(`${window.location.pathname}${window.location.search}`);
        await supabase.auth.resetPasswordForEmail(mail, {
          redirectTo: `${window.location.origin}/restablecer-clave`,
        });
        setResetCooldown(60);
        setMessage(
          'Si existe una cuenta con este email, vas a recibir instrucciones para restablecer tu contraseña.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error inesperado');
    } finally {
      setLoading(false);
    }
  };

  const feedback = (
    <>
      {error && (
        <div
          role="alert"
          className="w-full rounded-[12px] bg-[#FEE2E2] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#991B1B]"
        >
          {error}
        </div>
      )}
      {message && (
        <div
          className="w-full rounded-[12px] bg-[#D1FAE5] px-4 py-3 text-[13px] font-[500] leading-[18px] text-[#065F46]"
        >
          {message}
        </div>
      )}
    </>
  );

  // En la pantalla completa el título es el `h1` del documento; dentro del sheet
  // (diálogo) sigue siendo `h2`.
  const Heading = isPage ? 'h1' : 'h2';

  return (
    <div
      // `key={mode}`: al cambiar de modo el bloque se remonta y entra con un fade corto.
      key={mode}
      className={`flex w-full animate-fade-in flex-col gap-6 ${
        isPage
          ? 'mx-auto max-w-[440px] px-5 pb-10'
          : // El aire vertical del panel de desktop es FLUIDO —
            // ADR-2026-08-20-el-aire-del-panel-de-auth-cede-antes-que-el-scroll.
            //
            // Era `lg:py-16`: 64px arriba y 64 abajo, fijos. El panel izquierdo es
            // `overflow-y-auto` (`AuthSheet`), así que esos 128px se suman al contenido
            // y, cuando aparece el cartel de "ya iniciaste sesión en este dispositivo"
            // (+~90px), el form pasa de entrar justo a desbordar por unos pocos píxeles:
            // la mitad izquierda quedaba mínimamente scrolleable.
            //
            // `clamp(1.5rem, 6vh, 4rem)` mantiene los 64px donde hay lugar (≥1067px de
            // alto) y deja que el aire ceda hasta 24px en ventanas bajas, que es
            // exactamente lo que tiene que pasar: antes de scrollear, se achica el
            // respiro. `overflow-y-auto` queda de red para una ventana MUY baja.
            'p-5 lg:mx-auto lg:my-auto lg:max-w-[440px] lg:px-6 lg:py-[clamp(1.5rem,6vh,4rem)]'
      }`}
    >
      <div className="flex flex-col items-center gap-3">
        {/* Isotipo de Estudio Atlas arriba del título — igual en los tres modos. */}
        <img
          src="/images/estudio-atlas-logo-solo.svg"
          alt="Estudio Atlas"
          draggable={false}
          className={`w-auto select-none ${isPage ? 'h-12' : 'h-11 lg:h-14'}`}
        />
        <Heading
          id={titleId}
          className={`text-center font-[600] text-[#18181B] ${
            isPage
              ? 'text-[24px] leading-[30px]'
              : 'text-[20px] leading-[25px] lg:text-[28px] lg:leading-[34px]'
          }`}
        >
          {TITLES[mode]}
        </Heading>
      </div>

      {feedback}

      {mode === 'signup' && invitacion && (
        /* La otra cara del programa: quien llega por un link tiene que ver que
           llegó por un link. Sin esto, el amigo entra a un alta común y el
           incentivo que lo hizo tocar —los créditos extra— desaparece justo
           en el momento de decidir si se registra. */
        <div className="flex w-full items-center gap-3 rounded-xl border border-[#1644EB]/15 bg-[#1644EB]/[0.05] px-4 py-3">
          <span
            className="material-symbols-rounded shrink-0 text-[20px] text-[#1644EB]"
            aria-hidden="true"
          >
            redeem
          </span>
          <p className="text-[13px] leading-snug text-[#18181B]">
            {invitacion.nombre ? (
              <>
                <span className="font-semibold">{invitacion.nombre}</span> te
                invitó a Atlas.
              </>
            ) : (
              <span className="font-semibold">Te invitaron a Atlas.</span>
            )}{' '}
            {/* "se acreditan cuando actives tu plan" y no "arrancás con": el
                premio se paga contra el pago, no contra el alta. Decirlo acá
                cuesta algo de gancho, pero la alternativa es que el invitado
                entre buscando créditos que no están y le reclame a quien lo
                invitó. */}
            Tenés {invitacion.creditos} créditos esperándote: se acreditan
            cuando actives tu plan.
          </p>
        </div>
      )}

      {mode === 'signup' && (
        <>
          {/* Arriba de todo: el aviso vale para las DOS formas de registrarse, no
              sólo para el alta con correo. */}
          {ultimoLogin && (
            <AvisoCuentaNueva metodo={ultimoLogin} onIrALogin={() => switchMode('login')} />
          )}
          <GoogleButton
            label="Registrarme con Google"
            loading={googleLoading}
            onClick={handleGoogle}
          />
          <OrDivider />
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
            <div className="flex w-full flex-col gap-4">
              <Field
                id="signup-email"
                label="Correo electrónico"
                type="email"
                autoComplete="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={setEmail}
              />
              <PasswordField
                id="signup-password"
                value={password}
                onChange={setPassword}
                show={showPassword}
                onToggle={() => setShowPassword((s) => !s)}
                autoComplete="new-password"
              />
              <Field
                id="signup-name"
                label="Nombre y apellido"
                autoComplete="name"
                placeholder="Tu nombre"
                value={name}
                onChange={setName}
              />
            </div>
            <SubmitButton label="Registrarse con el correo" loading={loading} />
          </form>
          <Prompt
            text="¿Ya tienes una cuenta?"
            actionLabel="Iniciar sesión"
            onAction={() => switchMode('login')}
          />
          <span className="h-px w-full bg-[#E4E4E7]" />
          <p className="text-center text-[12px] font-[500] leading-[17px] text-[#18181B]">
            Al continuar, estás aceptando nuestros{' '}
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-[700] text-[#3B82F6] hover:underline"
            >
              términos y condiciones
            </a>
          </p>
        </>
      )}

      {mode === 'login' && (
        <>
          {/* Se marca UN solo camino: el que este dispositivo ya usó. Sin nada
              guardado, la pantalla queda igual que siempre. */}
          <GoogleButton
            label="Iniciar sesión con Google"
            loading={googleLoading}
            onClick={handleGoogle}
            destacado={ultimoLogin === 'google'}
          />
          <OrDivider />
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
            <div className="flex w-full flex-col gap-4">
              <Field
                id="login-email"
                label="Correo electrónico"
                type="email"
                autoComplete="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={setEmail}
                destacado={ultimoLogin === 'email'}
              />
              <div className="flex w-full flex-col gap-1.5">
                <PasswordField
                  id="login-password"
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggle={() => setShowPassword((s) => !s)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => switchMode('forgot')}
                  className="self-start text-[12px] font-[700] leading-[17px] text-[#3B82F6] hover:underline"
                >
                  Olvidé mi contraseña
                </button>
              </div>
            </div>
            <SubmitButton label="Iniciar sesión" loading={loading} />
          </form>
          <Prompt
            text="¿Todavía no tenes una cuenta?"
            actionLabel="Registrarme"
            onAction={() => switchMode('signup')}
          />
        </>
      )}

      {mode === 'forgot' && (
        <>
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6" noValidate>
            <Field
              id="forgot-email"
              label="Correo electrónico"
              type="email"
              autoComplete="email"
              placeholder="tu@correo.com"
              value={email}
              onChange={setEmail}
            />
            <SubmitButton label="Enviar" loading={loading} />
          </form>
          {/* Única salida del modo `forgot` en DESKTOP: ahí el form vive en el panel
              izquierdo fijo del split, que no tiene header donde poner el back ni X de
              cerrar (es `lg:hidden`). En mobile además está el back del header
              (ADR-2026-08-03-back-en-restablecer-contrasena). */}
          <Prompt
            text="¿Te acordaste de tu contraseña?"
            actionLabel="Iniciar sesión"
            onAction={() => switchMode('login')}
          />
        </>
      )}

      {/* Confirmación de email: Supabase creó el usuario pero NO la sesión, así que
          la cuenta no sirve hasta que abra el link del correo. Con reenvío y
          cooldown de 60s, igual que el `Auth.tsx` que este flujo reemplaza. */}
      {mode === 'confirmation' && (
        <>
          <p className="text-center text-[14px] font-[500] leading-[20px] text-[#52525C]">
            Te enviamos un correo a{' '}
            <span className="font-[700] text-[#18181B]">{email.trim()}</span>. Abrí el link
            que te mandamos para activar tu cuenta.
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || resendLoading}
            className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#F4F4F5] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#18181B] transition-colors hover:bg-[#E4E4E7] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {resendLoading && <Loader2 className="size-4 animate-spin" />}
            {resendCooldown > 0
              ? `Reenviar correo (${resendCooldown}s)`
              : 'Reenviar correo de confirmación'}
          </button>
          <Prompt
            text="¿Te equivocaste de correo?"
            actionLabel="Volver al registro"
            onAction={() => switchMode('signup')}
          />
        </>
      )}
    </div>
  );
}

// ─── Primitivos de formulario (tokens del DS) ────────────────────────────────

/**
 * Solapa que marca el camino de ingreso que este dispositivo ya usó
 * (ADR-2026-08-11-ultimo-metodo-de-ingreso). Sale del control que resalta: redondeada
 * arriba, recta abajo y sin separación, para que el borde de abajo apoye contra el
 * borde del container.
 *
 * El `mr-3` es igual al radio del container (12px): sin él la solapa terminaría sobre
 * la curva de la esquina derecha y se vería despegada.
 *
 * Va SIEMPRE dentro de un flex column sin gap y pegada al control de abajo.
 */
function UltimoUtilizadoBadge() {
  return (
    <span className="mr-3 self-end rounded-t-[8px] bg-[#DBEAFE] px-2 py-1 text-[11px] font-[700] leading-[14px] text-[#1E40AF]">
      Último utilizado
    </span>
  );
}

/**
 * Fila de input bordeada (Stroke/Strong #D4D4D8, radius-md), con focus de marca.
 *
 * `destacado` la pinta con el borde azul de "último utilizado". El `ring` es el mismo
 * recurso que el focus, así que no mueve el layout: el borde sigue midiendo 1px.
 */
function FieldRow({ children, destacado = false }: { children: ReactNode; destacado?: boolean }) {
  return (
    <div
      className={`flex items-center rounded-[12px] border px-4 transition-colors focus-within:border-[#1644EB] focus-within:ring-2 focus-within:ring-[#1644EB]/15 ${
        destacado ? 'border-[#1644EB] ring-2 ring-[#1644EB]/15' : 'border-[#D4D4D8]'
      }`}
    >
      {children}
    </div>
  );
}

/**
 * 16px en mobile, 14px (el del diseño) recién en `md:`.
 *
 * No es una preferencia estética: Safari en iOS hace **auto-zoom** al enfocar un input
 * con `font-size` menor a 16px, y ese zoom escala el viewport visual sin que el layout
 * se entere — la pantalla queda ampliada, corrida y con media pantalla en blanco (el
 * contenedor sigue midiendo el alto sin zoom). Es exactamente el síntoma reportado al
 * tocar el campo de contraseña. 16px es el umbral: con eso iOS no interviene.
 * Ver ADR-2026-08-03-inputs-16px-en-mobile-sin-auto-zoom.
 */
const INPUT_CLASS =
  'min-w-0 flex-1 bg-transparent py-3 text-[16px] font-[400] leading-[22px] md:text-[14px] md:leading-[19px] text-[#18181B] outline-none placeholder:text-[#71717B]';

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  /** Marca el campo como el camino de ingreso ya usado en este dispositivo. */
  destacado?: boolean;
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  destacado = false,
}: FieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
        {label}
      </label>
      {/* Sin `gap`: la solapa tiene que apoyar contra el borde del campo. */}
      <div className="flex w-full flex-col">
        {destacado && <UltimoUtilizadoBadge />}
        <FieldRow destacado={destacado}>
          <input
            id={id}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            className={INPUT_CLASS}
          />
        </FieldRow>
      </div>
    </div>
  );
}

interface PasswordFieldProps {
  id: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  autoComplete?: string;
}

function PasswordField({ id, value, onChange, show, onToggle, autoComplete }: PasswordFieldProps) {
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
        Contraseña
      </label>
      <FieldRow>
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••••••"
          autoComplete={autoComplete}
          className={`${INPUT_CLASS} auth-password-field`}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={show}
          className="ml-2 shrink-0 text-[#71717B] transition-colors hover:text-[#18181B]"
        >
          {show ? (
            <Icon name="outline/eye-off" size={18} />
          ) : (
            <Icon name="outline/eye" size={18} />
          )}
        </button>
      </FieldRow>
    </div>
  );
}

function GoogleButton({
  label,
  loading,
  onClick,
  destacado = false,
}: {
  label: string;
  loading: boolean;
  onClick: () => void;
  /** Marca el botón como el camino de ingreso ya usado en este dispositivo. */
  destacado?: boolean;
}) {
  return (
    // Sin `gap`: la solapa tiene que apoyar contra el borde del botón.
    <div className="flex w-full flex-col">
      {destacado && <UltimoUtilizadoBadge />}
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        // El borde azul se dibuja con `ring` y no con `border`: el botón no tiene
        // borde propio, así que agregarle uno lo haría 4px más alto sólo en este
        // estado. El `ring` es un box-shadow, no ocupa layout.
        //
        // `ring-inset` para que se dibuje HACIA ADENTRO: por fuera se saldría 2px
        // por encima del borde del botón y taparía el pie de la solapa.
        className={`flex w-full items-center justify-center gap-3 rounded-[12px] bg-[#F4F4F5] px-4 py-4 text-[16px] font-[700] leading-[21px] text-[#18181B] transition-colors hover:bg-[#E4E4E7] disabled:cursor-not-allowed disabled:opacity-60 ${
          destacado ? 'ring-2 ring-inset ring-[#1644EB]' : ''
        }`}
      >
        {loading ? (
          <Loader2 className="size-[18px] animate-spin text-[#71717B]" />
        ) : (
          <Icon name="color/google" size={18} />
        )}
        {label}
      </button>
    </div>
  );
}

function SubmitButton({ label, loading }: { label: string; loading: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#18181B] px-4 py-4 text-[15px] font-[700] leading-[19px] text-[#FAFAFA] transition-colors hover:bg-[#27272A] active:bg-[#09090B] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {label}
    </button>
  );
}

/**
 * Aviso del registro cuando en ESTE dispositivo ya se inició sesión alguna vez.
 *
 * Sólo aparece en ese caso: a un usuario genuinamente nuevo se le estaría advirtiendo
 * de una cuenta que no tiene, y el alta sin fricción es justo lo que queremos de él.
 * Nombra el método usado porque es lo que vuelve accionable el aviso — "andá a iniciar
 * sesión" sin decir con qué deja al usuario adivinando.
 */
function AvisoCuentaNueva({
  metodo,
  onIrALogin,
}: {
  metodo: MetodoLogin;
  onIrALogin: () => void;
}) {
  return (
    <div className="flex w-full flex-col gap-1 rounded-[12px] bg-[#DBEAFE] px-4 py-3">
      <p className="text-[13px] font-[700] leading-[18px] text-[#1E40AF]">
        Estás creando una cuenta nueva
      </p>
      <p className="text-[13px] font-[500] leading-[18px] text-[#1E40AF]">
        En este dispositivo ya iniciaste sesión con{' '}
        {metodo === 'google' ? 'Google' : 'tu correo'}.{' '}
        <button type="button" onClick={onIrALogin} className="font-[700] underline">
          Iniciá sesión
        </button>{' '}
        si querés entrar con esa cuenta.
      </p>
    </div>
  );
}

function OrDivider() {
  return (
    <div className="flex w-full items-center gap-3">
      <span className="h-px flex-1 bg-[#E4E4E7]" />
      <span className="text-[12px] font-[500] leading-[17px] text-[#52525C]">o</span>
      <span className="h-px flex-1 bg-[#E4E4E7]" />
    </div>
  );
}

function Prompt({
  text,
  actionLabel,
  onAction,
}: {
  text: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <p className="text-center text-[12px] font-[500] leading-[17px] text-[#18181B]">
      {text}{' '}
      <button
        type="button"
        onClick={onAction}
        className="font-[700] text-[#3B82F6] hover:underline"
      >
        {actionLabel}
      </button>
    </p>
  );
}

export default AuthForm;
